import {Component, OnDestroy, OnInit, ViewChild, inject} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';
import {forkJoin, Observable, ReplaySubject, Subject} from 'rxjs';
import {MatSelect, MatOption} from '@angular/material/select';
import {MatDialog} from '@angular/material/dialog';
import {IRib, Rib} from '../../shared/model/rib.model';
import {IEj} from '../../shared/model/ej.model';
import {filter, map, takeUntil} from 'rxjs/operators';
import {HttpClient, HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {ToastrService} from 'ngx-toastr';
import {MandatCreate} from '../../shared/model/mandat-create.model';
import {IbanAddDialogComponent} from './iban-add-dialog.component';
import {TelephoneAddDialogComponent} from './telephone-add-dialog.component';
import {AccountService} from "../core/auth/account.service";
import {ActivatedRoute, Router} from "@angular/router";
import {MandatSepa} from "../../shared/model/mandat-sepa.model";
import {ISummary} from "../../shared/model/summary.model";
import {MatCard, MatCardTitle} from '@angular/material/card';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatSelectSearchComponent} from 'ngx-mat-select-search';
import {MatMiniFabButton, MatButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {AsyncPipe} from '@angular/common';

@Component({
  selector: 'app-add-rib',
  templateUrl: './mandat-sign.component.html',
  styleUrls: ['./mandat-sign.component.css'],
  imports: [MatCard, MatCardTitle, FormsModule, ReactiveFormsModule, MatFormField, MatLabel, MatSelect, MatOption, MatSelectSearchComponent, MatMiniFabButton, MatIcon, MatButton, MatProgressSpinner, AsyncPipe]
})
export class MandatSignComponent implements OnInit, OnDestroy {
  dialog = inject(MatDialog);
  protected http = inject(HttpClient);
  private readonly toastr = inject(ToastrService);
  form = inject(UntypedFormBuilder);
  private readonly accountService = inject(AccountService);
  route = inject(ActivatedRoute);
  router = inject(Router);

  apiUrl = environment.apiUrl;
  isLoading = true;
  isLoadingMandat = false;
  public telephoneFilterCtrl: UntypedFormControl = new UntypedFormControl();
  public filteredTelephones: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);
  @ViewChild('telephoneSingleSelect', {static: true}) telephoneSingleSelect: MatSelect;
  public ibanFilterCtrl: UntypedFormControl = new UntypedFormControl();
  public filteredIbans: ReplaySubject<IRib[]> = new ReplaySubject<IRib[]>(1);
  @ViewChild('ibanSingleSelect', {static: true}) ibanSingleSelect: MatSelect;
  public ejFilterCtrl: UntypedFormControl = new UntypedFormControl();
  public filteredEjs: ReplaySubject<IEj[]> = new ReplaySubject<IEj[]>(1);
  @ViewChild('ejSingleSelect', {static: true}) ejSingleSelect: MatSelect;
  mandatCreate: MandatCreate = new MandatCreate();
  formGroup: UntypedFormGroup = this.form.group({
    ibans: new UntypedFormControl(this.mandatCreate.iban),
    ejs: new UntypedFormControl(this.mandatCreate.ej, [Validators.required]),
    telephones: new UntypedFormControl(this.mandatCreate.telephone, [Validators.required]),
  });
  raisonSociale: string;

  /* IBANs */
  protected ibans: IRib[] = [];
  /* EJs */
  protected ejs: IEj[] = [];
  private selectedEj: string = null;
  /* Telephones */
  protected telephones: string[] = [];
  protected _onDestroy = new Subject<void>();
  private lastPhone: string;

  ngOnInit(): void {
    this.selectedEj = this.route.snapshot.queryParamMap.get('ej');
    this.loadRaisonSociale(localStorage.getItem('tiers'));

    forkJoin([this.loadTelephones(), this.loadEjs(), this.loadIbans()])
      .subscribe(([telephones, ejs, ibans]) => {
        this.loadPreviousMandat();
        this.handleTelephones(telephones);
        this.handleEjs(ejs);
        this.handleIbans(ibans);
        this.isLoading = false;
      });
  }

  private static callApiMandat(mandatSign: MandatSepa): void {
    const reqApiMandat = {
      sessionid: mandatSign.sessionId,
      creditorid: mandatSign.creditorId,
      callerid: mandatSign.callerId
    };

    const form = document.createElement('form');
    document.body.appendChild(form);
    form.action = 'https://sign.epithete.com/fr/Emandats.aspx';
    form.method = 'POST';

    for (const name in reqApiMandat) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = reqApiMandat[name] as string;

      form.appendChild(input);
    }
    form.submit();
  }

  loadRaisonSociale(tiers: string): void {
    this.raisonSociale = null;
    if (tiers != null) {
      this.http.get<ISummary>(this.apiUrl + '/user/get-summary', {observe: 'response'})
        .pipe(
          filter((res: HttpResponse<ISummary>) => res.ok),
          map((res: HttpResponse<ISummary>) => res.body)
        )
        .subscribe({
          next: (summary: ISummary) => {
            this.raisonSociale = summary.businessName;
          },
          error: () => {
            // eslint-disable-next-line no-console
            console.log("Impossible de récupérer la raison social du client " + localStorage.getItem('tiers'));
          }
        });
    }
  }

  loadTelephones(): Observable<string[]> {
    return this.http.get<string[]>(this.apiUrl + '/user/telephones', {observe: 'response'})
      .pipe(
        filter((res: HttpResponse<string[]>) => res.ok),
        map((res: HttpResponse<string[]>) => res.body)
      );
  }

  handleTelephones(telephones: string[]): void {
    this.telephones = telephones;
    // load the initial bank list
    this.filteredTelephones.next(this.telephones.slice());
    if (this.telephones.length > 0) {
      this.mandatCreate.telephone = this.lastPhone ?? this.telephones[0];
    }

    // listen for search field value changes
    this.telephoneFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
          this.filterTelephones();
        }
      );
  }

  loadEjs(): Promise<IEj[]> {
    return this.accountService.fetchEjs();
  }

  handleEjs(ejs: IEj[]): void {
    this.ejs = ejs;
    // load the initial bank list
    this.filteredEjs.next(this.ejs.slice());
    if (this.selectedEj !== null && this.selectedEj !== undefined) {
      this.mandatCreate.ej = this.selectedEj;
    } else if (this.ejs.length > 0) {
      this.mandatCreate.ej = this.ejs[0].codeEj;
    }

    // listen for search field value changes
    this.ejFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
          this.filterEjs();
        }
      );
  }

  loadIbans(): Observable<IRib[]> {
    return this.http.get<IRib[]>(this.apiUrl + '/rib/user', {observe: 'response'})
      .pipe(
        filter((res: HttpResponse<IRib[]>) => res.ok),
        map((res: HttpResponse<IRib[]>) => res.body)
      );
  }

  handleIbans(ibans: IRib[]): void {
    this.ibans = ibans;
    // load the initial bank list
    this.filteredIbans.next(this.ibans.slice());

    if (this.ibans.length > 0) {
      this.mandatCreate.iban = this.ibans[0].codeIban;
    }
    // listen for search field value changes
    this.ibanFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
          this.filterIbans();
        }
      );
  }

  saveRib(rib: IRib): void {
    this.http.post<IRib>(this.apiUrl + '/rib/add', rib, {observe: 'response'})
      .pipe(
        filter((res: HttpResponse<IRib>) => res.ok),
        map((res: HttpResponse<IRib>) => res.body)
      ).subscribe({
      next: () => {
        this.loadIbans().subscribe((value) => {
          this.handleIbans(value);
          this.mandatCreate.iban = rib.codeIban;
        });
      },
      error: (res: HttpErrorResponse) => {
        if (res.status === 500) {
          this.showError(res.error.message ?? res.message);
        } else {
          this.showError("Une erreur est survenue");
        }
      }
    });
  }

  openDialogIban(): void {
    const dialogRef = this.dialog.open(IbanAddDialogComponent, {
      width: '100%',
      height: 'auto',
      data: new Rib()
    });

    dialogRef.afterClosed().subscribe((rib: IRib) => {
      if (rib !== undefined && rib.codeIban !== null) {
        this.saveRib(rib);
      }
    });
  }

  openDialogTelephone(): void {
    const dialogRef = this.dialog.open(TelephoneAddDialogComponent, {
      width: '100%',
      height: 'auto',
    });

    dialogRef.afterClosed().subscribe((phone: string) => {
      if (phone !== undefined) {
        this.telephones.push(phone);
        this.filteredTelephones.next(this.telephones.slice());
        this.mandatCreate.telephone = phone;
      }
    });
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  showError(error: string): void {
    this.toastr.error(error, 'Erreur', {
      timeOut: 3000
    });
  }

  onSubmit(): void {
    this.isLoadingMandat = true;
    if (this.mandatCreate.iban === undefined || this.mandatCreate.ej === undefined || this.mandatCreate.telephone === undefined) {
      this.isLoadingMandat = false;
      return;
    }
    this.mandatCreate.tiers = localStorage.getItem('tiers');

    this.http.post<MandatSepa>(this.apiUrl + '/mandat/create', this.mandatCreate, {observe: 'response'})
      .pipe(
        filter((res: HttpResponse<MandatSepa>) => res.ok),
        map((res: HttpResponse<MandatSepa>) => res.body)
      ).subscribe({
      next: (mandatReq: MandatSepa) => {
        if (mandatReq === null || (mandatReq.returnCode !== undefined && mandatReq.returnCode !== '0000') || mandatReq.sessionId === null || mandatReq.callerId === null || mandatReq.creditorId === null) {
          this.toastr.error('Une erreur est survenue, veuillez contacter le 3737');
          this.isLoadingMandat = false;
          return;
        }

        localStorage.setItem('sessionsInformations', JSON.stringify(mandatReq));
        this.toastr.success('Vous allez etre redirigé vers la signature du mandat');

        MandatSignComponent.callApiMandat(mandatReq);
      },
      error: (res: HttpErrorResponse) => {
        this.isLoadingMandat = false;
        if (res.status === 400) {
          this.showError(res.error.message ?? res.message);
        } else {
          this.showError("Une erreur est survenue");
        }
      }
    });
  }

  protected filterTelephones(): void {
    if (!this.telephones) {
      return;
    }
    // get the search keyword
    if (typeof this.telephoneFilterCtrl.value !== "string") {
      return;
    }
    let search = this.telephoneFilterCtrl.value;
    if (!search) {
      this.filteredTelephones.next(this.telephones.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    // filter the banks
    this.filteredTelephones.next(
      this.telephones.filter(telephone => telephone.trim().toLowerCase().includes(search))
    );
  }

  protected filterEjs(): void {
    if (!this.ejs) {
      return;
    }
    // get the search keyword
    if (typeof this.ejFilterCtrl.value !== "string") {
      return;
    }
    let search = this.ejFilterCtrl.value;
    if (!search) {
      this.filteredEjs.next(this.ejs.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    // filter the banks
    this.filteredEjs.next(
      this.ejs.filter(ej => ej.codeEj.toLowerCase().includes(search) || ej.libelle.toLowerCase().includes(search))
    );
  }

  protected filterIbans(): void {
    if (!this.ibans) {
      return;
    }
    // get the search keyword
    if (typeof this.ibanFilterCtrl.value !== "string") {
      return;
    }
    let search = this.ibanFilterCtrl.value;
    if (!search) {
      this.filteredIbans.next(this.ibans.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    // filter the banks
    this.filteredIbans.next(
      this.ibans.filter(rib => rib.codeIban.toLowerCase().includes(search))
    );
  }

  private loadPreviousMandat(): void {
    this.http.get<MandatSepa>(this.apiUrl + '/mandat/last', {observe: 'response'})
      .pipe(
        filter((res: HttpResponse<MandatSepa>) => res.ok),
        map((res: HttpResponse<MandatSepa>) => res.body)
      ).subscribe({
      next: (mandatSepa: MandatSepa) => {
        if (mandatSepa !== null && mandatSepa != undefined) {
          if (mandatSepa.DebitorPhone !== undefined && mandatSepa.DebitorPhone !== null) {
            this.lastPhone = mandatSepa.DebitorPhone.replace('+33', '0');
            this.telephones.push(this.lastPhone);
            //suppression de doublon
            this.telephones = this.telephones.filter((v, i, a) => a.indexOf(v) === i);
            this.filteredTelephones.next(this.telephones.slice());
            this.mandatCreate.telephone = this.lastPhone;
          }
        }
      },
      error:
        () => {
          this.toastr.error("une erreur est survenue");
        }
    });
  }
}

